import * as React from 'react';
import {
  NumberInputProps,
  Unstable_NumberInput as BaseNumberInput,
} from '@mui/base/Unstable_NumberInput';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { styled } from '@mui/system';

interface Props {
  onChange: (
    event:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => void;
  value: number;
  min?: number;
  max?: number;
  ariaLabel?: string;
}

const NumberInput = React.forwardRef(function CustomNumberInput(
  props: NumberInputProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return (
    <BaseNumberInput
      slotProps={{
        incrementButton: {
          children: <AddIcon fontSize="small" />,
          className: `increment ${props.value === props.max ? 'disabled' : ''}`,
        },
        decrementButton: {
          children: <RemoveIcon className="disabled" fontSize="small" />,
          className: props.value === props.min ? 'disabled' : '',
        },
      }}
      slots={{
        root: StyledInputRoot,
        input: StyledInput,
        incrementButton: StyledButton,
        decrementButton: StyledButton,
      }}
      {...props}
      ref={ref}
    />
  );
});

function QuantityInput({ ariaLabel, max, min, onChange, value }: Props) {
  return (
    <NumberInput aria-label={ariaLabel} max={max} min={min} onChange={onChange} value={value} />
  );
}

export default QuantityInput;

const blue = {
  100: '#daecff',
  200: '#b6daff',
  300: '#66b2ff',
  400: '#3399ff',
  500: '#007fff',
  600: '#0072e5',
  700: '#0059B2',
  800: '#004c99',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const StyledInputRoot = styled('div')(
  ({ theme }) => `
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[500]};
  display: inline-flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`,
);

const StyledInput = styled('input')(
  () => `
  font-size: 1rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.375;
  border: none;
  margin: 0 4px;
  padding: 10px 6px;
  outline: 0;
  min-width: 0;
  width: 1rem;
  text-align: center;

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus-visible {
    outline: 0;
  }
`,
);

const StyledButton = styled('button')(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  line-height: 1.5;
  border: 1px solid;
  border-radius: 999px;
  border-color: ${theme.palette.mode === 'dark' ? blue[700] : blue[700]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
  color: ${theme.palette.mode === 'dark' ? blue[700] : blue[700]};
  width: 32px;
  height: 32px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    cursor: pointer;
    background: ${theme.palette.mode === 'dark' ? blue[700] : blue[500]};
    border-color: ${theme.palette.mode === 'dark' ? blue[500] : blue[400]};
    color: ${grey[50]};
  }

  &:focus-visible {
    outline: 0;
  }

  &.increment {
    order: 1;
  }
  &.disabled {
    border-color: ${theme.palette.mode === 'dark' ? grey[500] : grey[400]};
    color: #999;
    cursor: not-allowed;
    &:hover {
      background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
    }
  }
`,
);
